import { useState, useEffect } from 'react';
import { submitWebform } from '../lib/cmsClient';
import FileUploadField from './FileUploadField';

export default function StickyCtaModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [formState, setFormState] = useState({
    Navn: '',
    Mail: '',
    Mobil: '',
    Adresse: '',
    Postnummer: '',
    Besked: '',
    Finansiering: false,
    AccepterBetingelser: true,
    website_url: '' // Obligatorisk Honeypot (Regel 12)
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    const handleOpen = () => setIsOpen(true);
    window.addEventListener('open-contact-modal', handleOpen);
    return () => window.removeEventListener('open-contact-modal', handleOpen);
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formState.AccepterBetingelser) {
      alert('Du skal acceptere vilkårene for at sende formularen.');
      return;
    }

    setSubmitting(true);
    setSubmitStatus(null);

    let res;
    if (files.length > 0) {
      const formData = new FormData();
      formData.append('Navn', formState.Navn);
      formData.append('Mail', formState.Mail);
      formData.append('email', formState.Mail); // Regel 12
      formData.append('Mobil', formState.Mobil);
      formData.append('Adresse', formState.Adresse);
      formData.append('Postnummer', formState.Postnummer);
      formData.append('Besked', formState.Besked);
      formData.append('Finansiering', formState.Finansiering ? 'Ja' : 'Nej');
      formData.append('website_url', formState.website_url);
      files.forEach((file) => {
        formData.append('billeder', file);
      });
      res = await submitWebform(formData);
    } else {
      const payload = {
        Navn: formState.Navn,
        Mail: formState.Mail,
        email: formState.Mail, // Regel 12
        Mobil: formState.Mobil,
        Adresse: formState.Adresse,
        Postnummer: formState.Postnummer,
        Besked: formState.Besked,
        Finansiering: formState.Finansiering ? 'Ja' : 'Nej',
        website_url: formState.website_url
      };
      res = await submitWebform(payload);
    }

    setSubmitting(false);

    if (res.ok) {
      setSubmitStatus('success');
      setFiles([]);
      setFormState({
        Navn: '',
        Mail: '',
        Mobil: '',
        Adresse: '',
        Postnummer: '',
        Besked: '',
        Finansiering: false,
        AccepterBetingelser: true,
        website_url: ''
      });
    } else {
      setSubmitStatus('error');
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSubmitStatus(null);
  };

  return (
    <>
      {/* Modal Overlay & Formular */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-backdrop-in"
          onClick={handleClose}
        >
          <div 
            className="relative w-full max-w-lg bg-white p-6 sm:p-8 shadow-2xl border border-neutral-200 overflow-y-auto max-h-[90vh] rounded-none text-[#0a0a0a]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Luk knap */}
            <button
              type="button"
              onClick={handleClose}
              className="absolute top-4 right-4 p-2 text-neutral-400 hover:text-neutral-900 transition-colors cursor-pointer"
              aria-label="Luk formular"
            >
              <i className="fa-solid fa-xmark text-xl"></i>
            </button>

            {/* Overskrift & beskrivelse */}
            <div className="mb-6 pr-6">
              <h3 className="font-['Saira'] font-bold text-2xl text-neutral-900 mb-1">
                Kontakt os
              </h3>
              <p className="font-['Open_Sans'] font-light text-sm text-neutral-600">
                Udfyld formularen nedenunder, så vender vi tilbage hurtigst muligt.
              </p>
            </div>

            {/* Succes-meddelelse */}
            {submitStatus === 'success' ? (
              <div className="py-8 text-center space-y-4">
                <div className="w-14 h-14 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto text-2xl">
                  <i className="fa-solid fa-check"></i>
                </div>
                <h4 className="font-['Saira'] font-bold text-xl text-neutral-900">
                  Tak for din henvendelse!
                </h4>
                <p className="font-['Open_Sans'] font-light text-sm text-neutral-600 max-w-sm mx-auto">
                  Vi har modtaget dine oplysninger og vender tilbage hurtigst muligt.
                </p>
                <button
                  type="button"
                  onClick={handleClose}
                  className="button mt-4"
                >
                  Luk vindue
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 font-['Open_Sans']">
                
                {/* Fejlmeddelelse */}
                {submitStatus === 'error' && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs font-medium">
                    Der opstod en fejl under afsendelsen. Prøv venligst igen eller ring direkte til os.
                  </div>
                )}

                {/* Navn */}
                <div>
                  <label htmlFor="modal-navn" className="block text-xs font-semibold text-neutral-800 mb-1">
                    Navn*
                  </label>
                  <input
                    type="text"
                    id="modal-navn"
                    name="Navn"
                    required
                    value={formState.Navn}
                    onChange={handleInputChange}
                    placeholder="Navn"
                    className="w-full px-3.5 py-2.5 text-sm border border-neutral-300 rounded-none focus:outline-none focus:border-neutral-900 bg-white"
                  />
                </div>

                {/* Mail */}
                <div>
                  <label htmlFor="modal-mail" className="block text-xs font-semibold text-neutral-800 mb-1">
                    Mail*
                  </label>
                  <input
                    type="email"
                    id="modal-mail"
                    name="Mail"
                    required
                    value={formState.Mail}
                    onChange={handleInputChange}
                    placeholder="Mail"
                    className="w-full px-3.5 py-2.5 text-sm border border-neutral-300 rounded-none focus:outline-none focus:border-neutral-900 bg-white"
                  />
                </div>

                {/* Mobil */}
                <div>
                  <label htmlFor="modal-mobil" className="block text-xs font-semibold text-neutral-800 mb-1">
                    Mobil*
                  </label>
                  <input
                    type="tel"
                    id="modal-mobil"
                    name="Mobil"
                    required
                    value={formState.Mobil}
                    onChange={handleInputChange}
                    placeholder="Mobil"
                    className="w-full px-3.5 py-2.5 text-sm border border-neutral-300 rounded-none focus:outline-none focus:border-neutral-900 bg-white"
                  />
                </div>

                {/* Adresse & Postnummer */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="modal-adresse" className="block text-xs font-semibold text-neutral-800 mb-1">
                      Adresse*
                    </label>
                    <input
                      type="text"
                      id="modal-adresse"
                      name="Adresse"
                      required
                      value={formState.Adresse}
                      onChange={handleInputChange}
                      placeholder="Adresse"
                      className="w-full px-3.5 py-2.5 text-sm border border-neutral-300 rounded-none focus:outline-none focus:border-neutral-900 bg-white"
                    />
                  </div>
                  <div>
                    <label htmlFor="modal-postnummer" className="block text-xs font-semibold text-neutral-800 mb-1">
                      Postnummer*
                    </label>
                    <input
                      type="text"
                      id="modal-postnummer"
                      name="Postnummer"
                      required
                      value={formState.Postnummer}
                      onChange={handleInputChange}
                      placeholder="Postnummer"
                      className="w-full px-3.5 py-2.5 text-sm border border-neutral-300 rounded-none focus:outline-none focus:border-neutral-900 bg-white"
                    />
                  </div>
                </div>

                {/* Besked */}
                <div>
                  <label htmlFor="modal-besked" className="block text-xs font-semibold text-neutral-800 mb-1">
                    Besked*
                  </label>
                  <textarea
                    id="modal-besked"
                    name="Besked"
                    required
                    rows={3}
                    value={formState.Besked}
                    onChange={handleInputChange}
                    placeholder="Besked"
                    className="w-full px-3.5 py-2.5 text-sm border border-neutral-300 rounded-none focus:outline-none focus:border-neutral-900 bg-white resize-y"
                  />
                </div>

                {/* Vedhæft filer */}
                <FileUploadField
                  files={files}
                  onFilesChange={setFiles}
                  idPrefix="modal"
                  label="Vedhæft evt. 1-5 billeder af dit hus på afstand, hvor man kan se taget"
                />

                {/* Checkbox: Finansiering på plads */}
                <div className="pt-1">
                  <label className="flex items-start gap-2.5 cursor-pointer text-xs text-neutral-700">
                    <input
                      type="checkbox"
                      name="Finansiering"
                      checked={formState.Finansiering}
                      onChange={handleInputChange}
                      className="mt-0.5 rounded-none border-neutral-300 text-[#e70000] focus:ring-0 cursor-pointer"
                    />
                    <span>Har du finansieringen på plads og ønsker et tilbud?</span>
                  </label>
                </div>

                {/* Checkbox: Accepter betingelser* */}
                <div>
                  <label className="flex items-start gap-2.5 cursor-pointer text-xs text-neutral-700">
                    <input
                      type="checkbox"
                      name="AccepterBetingelser"
                      required
                      checked={formState.AccepterBetingelser}
                      onChange={handleInputChange}
                      className="mt-0.5 rounded-none border-neutral-300 text-[#e70000] focus:ring-0 cursor-pointer"
                    />
                    <span>Jeg har læst og accepteret vilkårene for brug af oplysninger og privatlivspolitik.*</span>
                  </label>
                </div>

                {/* Antispam Honeypot (Regel 12) */}
                <div aria-hidden="true" className="sr-only absolute -left-[9999px]">
                  <label htmlFor="modal-website-url">Website URL</label>
                  <input
                    type="text"
                    id="modal-website-url"
                    name="website_url"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formState.website_url}
                    onChange={handleInputChange}
                  />
                </div>

                {/* Send Knap */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="button w-full cursor-pointer disabled:opacity-50"
                  >
                    {submitting ? 'Sender...' : 'Send'}
                  </button>
                </div>

              </form>
            )}

          </div>
        </div>
      )}
    </>
  );
}
